import {Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';

import {Subject, Subscription} from 'rxjs';

import {Helper} from '../../common/classes/helper';
import {XlsxHelper} from '../../common/classes/xlsx.helper';
import {ReportExtensions} from '../../common/classes/report.extensions';

import {
    IAccountTradesReport,
    IAccountTradesRow,
    IKeyValue,
    IReport,
    ReportColumnNames,
    ITradesDateViewData,
    ITradesSymbolViewData,
    TradesViewDataInterface
} from '../../interfaces';

import {
    DataHeader,
    DataColumn,
    DataRow,
    IDataRow,
    IDataTable
} from '../reporting-table';

import {TradesReportTableConfig} from '../../descriptors';

@Component({
    selector: 'reporting-trades-report',
    templateUrl: './trades-report.component.html',
    styleUrls: ['./trades-report.component.scss'],
})
export class TradesReportComponent implements OnInit, OnDestroy, OnChanges {
    private exportTriggerSubscription: Subscription;

    public viewData: TradesViewDataInterface;
    public noDataTableConfig: IDataTable;
    @Input() public reportTableConfig: IDataTable;

    @ViewChild('reportsContainer') public reportsContainer: ElementRef;

    @Input() public dateFormat: string;
    @Input() public timeFormat: string;
    @Input() public secondaryTable: boolean;
    @Input() public dateTimeFormat: string;
    @Input() public reportDateDifference: number;
    @Input() public reportData: IAccountTradesReport;
    @Input() public exportTrigger: Subject<string>;

    constructor() {
        this.viewData = {
            dates: {},
            totalRows: null
        };

        this.dateFormat = Helper.getDefaultDateFormat();
    }

    private getData(data: IAccountTradesReport): TradesViewDataInterface {
        const groupedDetailedReport: TradesViewDataInterface = {
            dates: {},
            totalRows: []
        };

        if (data.totals.length > 0) {
            const totalRow = this.initReportAndDailyTotalRow(data.totals[0]);

            groupedDetailedReport.totalRows.push(totalRow);
        }

        data.items.forEach(item => {
            groupedDetailedReport.dates[item.date.toString()] = this.initDate(item);
        });

        return groupedDetailedReport;
    }

    private initDate(item: IReport<IAccountTradesRow>): ITradesDateViewData {
        const date = {
            symbols: {}
        } as ITradesDateViewData;

        const symbols = {
            tradesTableConfig: {
                columns: this.reportTableConfig.columns,
                rows: [],
                hiddenColumnIds: []
            }
        } as ITradesSymbolViewData;

        item.rows.forEach(itemRow => {
            const row = new DataRow({rowId: null, dataItems: []});

            row.dataItems = this.reportTableConfig.columns.map((column: DataHeader) => {
                const value = this.getTradeItemValue(column.selector, itemRow);

                return new DataColumn({
                    colId: column.selector,
                    value,
                    hidden: false,
                    hasNegativeValue: this.checkItemForNegativeValue(column.selector, itemRow),
                    shifted: this.isShiftedTableData(column.selector),
                    hasIcon: column.selector === ReportColumnNames.Gross
                });
            });

            symbols.tradesTableConfig.rows.push(row);
        });

        date.symbols.X = symbols;

        date.dateTotalRows = [this.initReportAndDailyTotalRow(item.totals[0])];

        return date;
    }

    private initReportAndDailyTotalRow(
        summary: IKeyValue<string, IAccountTradesRow>,
    ) {
        const row: IDataRow = {rowId: null, dataItems: []};

        row.dataItems = this.reportTableConfig.columns.map((column: DataHeader) => {
            const value = this.mapDailyTotalRowData(column.selector, summary.value, summary.key);

            return new DataColumn({
                colId: column.selector,
                value,
                hidden: false,
                shifted: (column.selector !== ReportColumnNames.Date),
                hasIcon: (column.selector === ReportColumnNames.Gross),

                hasNegativeValue: this.checkItemForNegativeValue(column.selector, summary.value)
            });
        });

        return new DataRow(row);
    }

    private checkItemForNegativeValue(
        colId: string,
        summary: IAccountTradesRow
    ): boolean {
        switch (colId) {
            case ReportColumnNames.Gross:
                return ReportExtensions.isNegative(summary.gross);
            case ReportColumnNames.COMM:
                return ReportExtensions.isNegative(summary.comm);
            case ReportColumnNames.ECNFee:
                return ReportExtensions.isNegative(summary.ecnFee);
            case ReportColumnNames.SEC:
                return ReportExtensions.isNegative(summary.sec);
            case ReportColumnNames.TAF:
                return ReportExtensions.isNegative(summary.taf);
            case ReportColumnNames.NSCC:
                return ReportExtensions.isNegative(summary.nscc);
            case ReportColumnNames.CLR:
                return ReportExtensions.isNegative(summary.clr);
            case ReportColumnNames.ORF:
                return ReportExtensions.isNegative(summary.orf);
            case ReportColumnNames.PTFPF:
                return ReportExtensions.isNegative(summary.ptfpf);
            case ReportColumnNames.EFTT:
                return ReportExtensions.isNegative(summary.eftt);
            case ReportColumnNames.CAT:
                return ReportExtensions.isNegative(summary.cat);
            case ReportColumnNames.Net:
                return ReportExtensions.isNegative(summary.net);
            default:
                return false;
        }
    }

    private mapDailyTotalRowData(
        colId: string,
        item: IAccountTradesRow,
        totalKey: string): string | number {
        switch (colId) {
            case ReportColumnNames.Date:
                return totalKey;
            case ReportColumnNames.Qty:
                return ReportExtensions.toDecimalString(item.qty, 3, 3, true, true);
            case ReportColumnNames.Gross:
                return ReportExtensions.toDecimalString(item.gross);
            case ReportColumnNames.Net:
                return ReportExtensions.toDecimalString(item.net);
            case ReportColumnNames.COMM:
                return ReportExtensions.toDecimalString(item.comm);
            case ReportColumnNames.ECNFee:
                return ReportExtensions.toDecimalString(item.ecnFee);
            case ReportColumnNames.SEC:
                return ReportExtensions.toDecimalString(item.sec);
            case ReportColumnNames.TAF:
                return ReportExtensions.toDecimalString(item.taf);
            case ReportColumnNames.NSCC:
                return ReportExtensions.toDecimalString(item.nscc);
            case ReportColumnNames.CLR:
                return ReportExtensions.toDecimalString(item.clr);
            case ReportColumnNames.ORF:
                return ReportExtensions.toDecimalString(item.orf);
            case ReportColumnNames.PTFPF:
                return ReportExtensions.toDecimalString(item.ptfpf);
            case ReportColumnNames.EFTT:
                return ReportExtensions.toDecimalString(item.eftt);
            case ReportColumnNames.CAT:
                return ReportExtensions.toDecimalString(item.cat);
            default:
                return '';
        }
    }

    private getTradeItemValue(colId: string, item: IAccountTradesRow): string | number {
        switch (colId) {
            case ReportColumnNames.Opened:
            case ReportColumnNames.Date:
                return !item.opened ? '' : Helper.formatDate(item.opened, this.dateTimeFormat, false);
            case ReportColumnNames.Closed:
                return !item.closed ? '' : Helper.formatDate(item.closed, this.timeFormat, false);
            case ReportColumnNames.Held:
            case ReportColumnNames.HeldFormatted:
                return item.heldFormatted ?? '';
            case ReportColumnNames.Symbol:
                return item.symbol ?? '';
            case ReportColumnNames.Type:
                return item.type ?? '';
            case ReportColumnNames.Entry:
                return ReportExtensions.toDecimalString(item.entry, 4);
            case ReportColumnNames.Exit:
                return ReportExtensions.toDecimalString(item.exit, 4);
            default:
                break;
        }

        return this.mapDailyTotalRowData(colId, item, '');
    }

    private isShiftedTableData(colId: string): boolean {
        const shiftedColumns: string[] = [
            ReportColumnNames.Date,
            ReportColumnNames.Closed,
            ReportColumnNames.Held,
            ReportColumnNames.HeldFormatted,
            ReportColumnNames.Symbol,
            ReportColumnNames.Type
        ];

        return shiftedColumns.indexOf(colId) < 0;
    }

    ngOnInit(): void {
        if (this.reportTableConfig == null) {
            this.reportTableConfig = TradesReportTableConfig();
        }

        if (this.reportData && this.reportData.items.length > 0) {
            this.viewData = this.getData(this.reportData);
        } else {
            this.noDataTableConfig = this.reportTableConfig;
        }

        if (this.exportTrigger) {
            this.exportTriggerSubscription = this.exportTrigger.subscribe((reportName: string) => {
                if (this.reportsContainer) {
                    const reportTablesLabels = this.reportTableConfig.columns.map((column: DataHeader) => column.label);

                    XlsxHelper.generateXlsxForContainerWithId(this.reportsContainer, reportTablesLabels, reportName);
                }
            });
        }
    }

    ngOnDestroy(): void {
        if (this.exportTriggerSubscription) {
            this.exportTriggerSubscription.unsubscribe();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        Object.keys(changes).map((key: string) => {
            if (
                (typeof changes[key] !== 'undefined') &&
                !changes[key].firstChange &&
                this.reportData
            ) {
                this.viewData = this.getData(this.reportData);
            }
        });
    }

    trackByIndex(index: number): number {
        return index;
    }
}
